/**
 * Model "class" for Participant - defines useful field metadata for building and validating forms
 */

import * as _ from 'lodash'
import { required, helpers } from 'vuelidate/lib/validators'
import Api from '@/providers/Api'
import User from '@/apps/default/User'

// custom validator for (optional) phone numbers - 123-123-1234
const phoneNumber = (value) => {
    return !helpers.req(value) || value === '' || /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/.test(value)
}

export default {
    fields: {
        email: {
            label: 'FIELD_USER_EMAIL',
            type: 'email',
            validations: {
                required: required,
                uniqueEmail: function (value) {
                    if (!value || value === '') return true

                    // 'this' points to the DataForm component (vuelidate.js.org/#sub-accessing-component)
                    const participant = this.modelInstance

                    return Api.findParticipants(null, value)
                        .then((items) => {
                            if (!items.length) {
                                return true // no matches found
                            } else if (items[0].id === participant.id) {
                                return true // match found with the participant we're editing
                            } else {
                                return false // match found with some other participant
                            }
                        })
                }
            },
            invalidFeedback: { uniqueEmail: 'MESSAGE_EMAIL_IN_USE', required: 'MESSAGE_FIELD_REQUIRED' }
        },
        external_id: {
            label: 'FIELD_PARTICIPANT_EXTERNAL_ID',
            type: 'text',
            validations: { maxLength: 255 },
        },
        plan_id: {
            label: 'FIELD_PARTICIPANT_PLAN',
            type: 'select',
            loadOptions: () => {
                if (User.practitionerPracticeId()) {
                    // only show plans for current practice
                    const filters = { practice_id: { field: 'practice_id', value: User.practitionerPracticeId() } }
                    const expands = ['plan']
                    return Api.getItems(null, 'plan-practice', filters, {}, expands)
                        .then((items) => {
                            return _.map(items, (pp) => {
                                return {
                                    value: pp.plan.id, text: pp.plan.name
                                }
                            })
                        })
                } else if (User.roleIsResellerAdmin()) {
                    const filters = { reseller_id: { field: 'reseller_id', value: User.reseller().id } }
                    return Api.getItems(null, 'plan', filters, {})
                        .then((plans) => {
                            return _.map(plans, (plan) => {
                                return {
                                    value: plan.id, text: plan.company.name + ' - ' + plan.name
                                }
                            })
                        })
                } else {
                    // show all plans (for admin)
                    return Api.getAllItems(null, 'plan')
                        .then((plans) => {
                            return _.map(plans, (plan) => {
                                return {
                                    value: plan.id, text: plan.name
                                }
                            })
                        })
                }
            },
            validations: { required }
        },
        emr_id: {
            label: 'FIELD_PARTICIPANT_EMR_ID',
            labelSlot: 'emr',
            type: 'text',
            validations: { maxLength: 127 },
        },
        firstname: {
            label: 'FIELD_PARTICIPANT_FIRSTNAME',
            type: 'text',
            validations: { required }
        },
        lastname: {
            label: 'FIELD_PARTICIPANT_LASTNAME',
            type: 'text',
            validations: { required },
        },
        dob: {
            label: 'FIELD_PARTICIPANT_DOB',
            type: 'date',
            validations: { required },
        },
        gender: { // i.e. gender assigned at birth
            label: 'FIELD_PARTICIPANT_GENDER',
            type: 'radios',
            options: {
                M: 'FIELD_PARTICIPANT_GENDER_MALE',
                F: 'FIELD_PARTICIPANT_GENDER_FEMALE',
                UNKNOWN: 'FIELD_PARTICIPANT_GENDER_UNKNOWN'
            },
            validations: { required },
        },
        gender_identity: {
            label: 'FIELD_PARTICIPANT_GENDER_IDENTITY',
            type: 'select',
            options: {
                MALE: 'FIELD_PARTICIPANT_GENDER_IDENTITY_MALE',
                FEMALE: 'FIELD_PARTICIPANT_GENDER_IDENTITY_FEMALE',
                TRANS_FTM: 'FIELD_PARTICIPANT_GENDER_IDENTITY_TRANS_FTM',
                TRANS_MTF: 'FIELD_PARTICIPANT_GENDER_IDENTITY_TRANS_MTF',
                NON_BINARY: 'FIELD_PARTICIPANT_GENDER_IDENTITY_NON_BINARY',
                UNDISCLOSED: 'FIELD_PARTICIPANT_GENDER_IDENTITY_UNDISCLOSED',
                OTHER: 'FIELD_PARTICIPANT_GENDER_IDENTITY_OTHER',
            },
        },
        sexual_orientation: {
            label: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION',
            type: 'select',
            options: {
                HOMOSEXUAL: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_HOMOSEXUAL',
                HETEROSEXUAL: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_HETEROSEXUAL',
                BISEXUAL: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_BISEXUAL',
                OTHER: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_OTHER',
                UNKNOWN: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_UNKNOWN',
                UNDISCLOSED: 'FIELD_PARTICIPANT_SEXUAL_ORIENTATION_UNDISCLOSED',
            },
        },
        pronouns: {
            label: 'FIELD_PARTICIPANT_PRONOUNS',
            type: 'text',
            validations: { maxLength: 255 },
        },
        ethnicity: {
            label: 'FIELD_PARTICIPANT_ETHNICITY',
            type: 'select',
            options: {
                NATIVE: 'FIELD_PARTICIPANT_ETHNICITY_NATIVE',
                ASIAN: 'FIELD_PARTICIPANT_ETHNICITY_ASIAN',
                AFRICAN: 'FIELD_PARTICIPANT_ETHNICITY_AFRICAN',
                HISPANIC: 'FIELD_PARTICIPANT_ETHNICITY_HISPANIC',
                PACIFIC: 'FIELD_PARTICIPANT_ETHNICITY_PACIFIC',
                WHITE: 'FIELD_PARTICIPANT_ETHNICITY_WHITE',
            },
            // nullOptionLabel: 'FIELD_GOAL_TIMEFRAME_PROMPT',
            validations: { required }
        },
        phone_primary: {
            label: 'FIELD_PARTICIPANT_PHONE_PRIMARY',
            type: 'tel',
            validations: { phoneNumber: phoneNumber },
            invalidFeedback: { phoneNumber: 'MESSAGE_PHONE_NUMBER_FORMAT' }
        },
        phone_secondary: {
            label: 'FIELD_PARTICIPANT_PHONE_SECONDARY',
            type: 'tel',
            validations: { phoneNumber: phoneNumber },
            invalidFeedback: { phoneNumber: 'MESSAGE_PHONE_NUMBER_FORMAT' }
        },
        preferred_contact_method: {
            label: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD',
            type: 'checks',
            options: {
                CALL_PRIMARY: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD_CALL_PRIMARY',
                TEXT_PRIMARY: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD_TEXT_PRIMARY',
                CALL_SECONDARY: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD_CALL_SECONDARY',
                TEXT_SECONDARY: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD_TEXT_SECONDARY',
                EMAIL: 'FIELD_PARTICIPANT_PREFERRED_CONTACT_METHOD_EMAIL',
            },
            validations: { required }
        },
        contact_privacy_waived: {
            label: 'FIELD_PARTICIPANT_CONTACT_PRIVACY_WAIVED',
            type: 'radios',
            options: {
                1: 'FIELD_PARTICIPANT_CONTACT_PRIVACY_WAIVED_YES',
                0: 'FIELD_PARTICIPANT_CONTACT_PRIVACY_WAIVED_NO',
            },
        },
        address: {
            label: 'FIELD_PARTICIPANT_ADDRESS',
            type: 'text',
        },
        address2: {
            label: 'FIELD_PARTICIPANT_ADDRESS2',
            type: 'text',
        },
        city: {
            label: 'FIELD_PARTICIPANT_CITY',
            type: 'text',
        },
        state: {
            label: 'FIELD_PARTICIPANT_STATE',
            type: 'text',
        },
        zip: {
            label: 'FIELD_PARTICIPANT_ZIP',
            type: 'text',
        },
        disabled: {
            label: 'FIELD_PARTICIPANT_DISABLED',
            type: 'check',
        },
    }
}
